import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { authHeader, readApiJson } from '../api/auth';
import './PrivatePaperBot.css';

type Profile = { user_id: string; enabled: boolean; initial_bank: number; win_stake: number; place_stake: number;
  win_enabled: boolean; place_enabled: boolean; revision: number; last_tick_at?: string; last_error?: string;
  upcoming_candidates?: number };
type RecordRow = { _id: string; runner: string; track: string; race: string; market_type: string;
  policy_id: string; stake: number; pnl?: number; bsp?: number; state: string; reason: string; start_time: string };
type Status = { profile: Profile; records: RecordRow[]; pagination: { page: number; pages: number; total: number };
  summary: { balance: number; available: number; pnl: number; turnover: number; open_stake: number;
    settled: number; roi: number | null }; checked_at: string };
const units = (n: number) => new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const time = (s?: string) => s ? new Date(s).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' }) : 'Not yet';

export default function PrivatePaperBot({ userId, historyOnly = false }: { userId: string; historyOnly?: boolean }) {
  const [data, setData] = useState<Status | null>(null);
  const [draft, setDraft] = useState<Profile | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const dirty = useRef(false);
  const request = useRef<AbortController | null>(null);
  const alive = useRef(true);
  const load = useCallback(async () => {
    request.current?.abort();
    const ac = new AbortController(); request.current = ac;
    try {
      const headers = authHeader();
      const response = await fetch(`${API_BASE_URL}/api/paper-bot?page=${page}`, { headers, cache: 'no-store', signal: ac.signal });
      const next: Status = await readApiJson(response, headers.Authorization);
      if (ac.signal.aborted || !alive.current) return;
      if (next.profile.user_id !== userId) throw new Error('Profile mismatch. Please sign in again.');
      setData(next); if (!dirty.current) setDraft(next.profile); setError('');
    } catch (e: any) { if (!ac.signal.aborted && alive.current) setError(e.message); }
  }, [userId, page]);
  useEffect(() => {
    alive.current = true; load();
    const timer = window.setInterval(load, 30000);
    return () => { alive.current = false; request.current?.abort(); window.clearInterval(timer); };
  }, [load]);
  function change(patch: Partial<Profile>) {
    dirty.current = true; setNotice(''); setDraft(d => d ? { ...d, ...patch } : d);
  }
  async function save() {
    if (!draft) return;
    setBusy(true); setNotice(''); setError('');
    try {
      const { revision, enabled, initial_bank, win_stake, place_stake, win_enabled, place_enabled } = draft;
      const headers: Record<string, string> = { ...authHeader(), 'Content-Type': 'application/json' };
      const response = await fetch(`${API_BASE_URL}/api/paper-bot/settings`, { method: 'POST', headers,
        body: JSON.stringify({ revision, enabled, initial_bank, win_stake, place_stake, win_enabled, place_enabled }) });
      const next: Profile = await readApiJson(response, headers.Authorization);
      if (!alive.current) return;
      if (next.user_id !== userId) throw new Error('Profile mismatch. Please sign in again.');
      dirty.current = false; setDraft(next); setNotice('Your paper settings have been saved.'); await load();
    } catch (e: any) { if (alive.current) setError(e.message); }
    finally { if (alive.current) setBusy(false); }
  }
  return <section className="private-paper">
    <header><div><span className="paper-badge">PAPER ONLY</span><h1>{historyOnly ? 'My paper results' : 'My private paper bot'}</h1></div>
      <button onClick={() => load()}>Refresh results</button></header>
    <p>Virtual units only. No real-money orders, account connection or live switch. Settings and results belong only to your profile.</p>
    <p>{historyOnly ? <Link to="/strategy/auto">Paper bot controls</Link> : <Link to="/my-bets">My paper results</Link>}</p>
    {error && <div className="paper-error" role="alert">{error} <button onClick={() => { dirty.current = false; load(); }}>Reload saved settings</button></div>}
    {notice && <p role="status">{notice}</p>}
    {!data && !error && <p>Loading your private paper profile...</p>}
    {data && <>
      <div className="paper-stats">
        {([['Virtual balance', units(data.summary.balance)], ['Available units', units(data.summary.available)],
          ['Simulated profit / loss', units(data.summary.pnl)], ['Open simulated stakes', units(data.summary.open_stake)],
          ['Settled simulations', String(data.summary.settled)],
          ['Simulated ROI', data.summary.roi === null ? '-' : `${data.summary.roi}%`]] as [string, string][])
          .map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}
      </div>
      {!historyOnly && draft && <form className="paper-settings" onSubmit={e => { e.preventDefault(); save(); }}>
        <h2>My simulation controls</h2><p><Link to="/strategy/bucket-bets">Choose my paper buckets and calibrate their stakes</Link>. Saved bucket settings take priority over WIN/PLACE amounts below.</p>
        <label><input type="checkbox" checked={draft.enabled} disabled={busy} onChange={e => change({ enabled: e.target.checked })} /> Run my paper bot</label>
        <div className="paper-fields">
          <label>Opening virtual bank<input type="number" min="1" max="1000000" step="0.01" required
            value={draft.initial_bank} disabled={busy || data.pagination.total > 0} onChange={e => change({ initial_bank: Number(e.target.value) })} /></label>
          <label>WIN stake (units)<input type="number" min="1" max="1000000" step="0.01" required
            value={draft.win_stake} disabled={busy} onChange={e => change({ win_stake: Number(e.target.value) })} /></label>
          <label>PLACE stake (units)<input type="number" min="1" max="1000000" step="0.01" required
            value={draft.place_stake} disabled={busy} onChange={e => change({ place_stake: Number(e.target.value) })} /></label>
        </div>
        <div className="paper-fields">
          <label><input type="checkbox" checked={draft.win_enabled} disabled={busy} onChange={e => change({ win_enabled: e.target.checked })} /> Include WIN simulations</label>
          <label><input type="checkbox" checked={draft.place_enabled} disabled={busy} onChange={e => change({ place_enabled: e.target.checked })} /> Include PLACE simulations</label>
        </div>
        <button type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save my settings'}</button>
        <p>Fixed stakes. Opening bank is locked after the first record to preserve history. Pausing stops new simulations; existing ones still settle.</p>
      </form>}
      <div className="paper-explanation">
        <strong>{data.profile.enabled ? 'Paper bot running' : 'Paper bot stopped'}</strong>
        <p>Uses only your own saved strategy selections. No admin bets, bank or results are copied. This is a separate simulator, not a replica of the admin staking engine.</p>
        <p>Enable before five minutes to race start. Eligible horse-racing WIN/PLACE selections are recorded in the final five-minute window, never after the cutoff. No historical backfill.</p>
        <p>Settles from cached official outcomes and BSP, with a simulated 10% charge on positive gross returns. Missing outcomes or ambiguous dead heats remain pending.</p>
        <p>Last processing check: {time(data.profile.last_tick_at)}. Upcoming eligible selections in your feed: {data.profile.upcoming_candidates ?? 0}.</p>
        {data.profile.last_error && <p role="alert">{data.profile.last_error}</p>}
        {!data.profile.upcoming_candidates && <p>No upcoming selections in your private feed. Save your own card on the <Link to="/strategy">strategy page</Link>; your admin's card is not used.</p>}
      </div>
      <h2>My simulation history</h2>
      {!data.records.length ? <p>No paper records yet. No real bets have been placed by this simulator.</p> : <div className="paper-table-wrap"><table>
        <thead><tr><th>Race time (Sydney)</th><th>Selection</th><th>Source</th><th>Market</th><th>Units</th><th>Status</th><th>Simulated P/L</th></tr></thead>
        <tbody>{data.records.map(r => <tr key={r._id}>
          <td>{time(r.start_time)}</td><td>{r.runner}<small>{r.track} {r.race ? `R${r.race}` : ''}</small></td>
          <td>{r.policy_id}</td><td>{r.market_type}</td><td>{units(r.stake)}</td><td>{r.state.replace(/_/g, ' ')}<small>{r.reason}</small></td>
          <td>{r.pnl === undefined ? '-' : units(r.pnl)}</td>
        </tr>)}</tbody>
      </table></div>}
      {data.pagination.pages > 1 && <div className="paper-pagination">
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
        <span>Page {page} of {data.pagination.pages}</span>
        <button disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}>Next</button>
      </div>}
    </>}
  </section>;
}
